import { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Slider from '@mui/material/Slider';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SettingsIcon from '@mui/icons-material/Settings';
import axiosInstance, { useAuth } from '../../authentication/AuthContext';

export default function PreferencesGrid() {
    const navigate = useNavigate();
    const { accessToken } = useAuth();
    const [loading, setLoading] = useState(true);
    const [preferences, setPreferences] = useState({
        chunks_to_retrieve: 5,
        answer_style: 'concise',
        show_citations: false,
        response_mode: 'generation',
    });

    useEffect(() => {
        const loadPreferences = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get('/preferences/', {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    withCredentials: true,
                });
                const data =
                    response?.data?.preferences ?? response?.data ?? {};
                setPreferences((prev) => ({
                    ...prev,
                    ...data,
                }));
            } catch (error) {
                console.error('Error loading dashboard preferences:', error);
            } finally {
                setLoading(false);
            }
        };

        if (accessToken) {
            loadPreferences();
        } else {
            setLoading(false);
        }
    }, [accessToken]);

    const answerStyleLabel = {
        concise: 'Concise',
        detailed: 'Detailed',
        conversational: 'Conversational',
        technical: 'Technical',
    };
    const responseModeLabel = {
        generation: 'Generation',
        retrieval: 'Retrieval Only',
    };
    const chunksDisplayValue = Math.min(
        20,
        Math.max(1, Number(preferences.chunks_to_retrieve) || 1)
    );

    const handleEditSettings = () => {
        navigate('/preferences');
    };

    return (
        <Card variant="outlined" sx={{ flex: 1 }}>
            <CardContent
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <Typography variant="h6">Preferences</Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={handleEditSettings}
                        sx={{ minWidth: 'auto' }}
                    >
                        Edit
                    </Button>
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        overflow: 'auto',
                    }}
                >
                    {loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 2,
                            }}
                        >
                            <CircularProgress size={22} />
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ mb: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 1, fontWeight: 500 }}
                                >
                                    Chunks to retrieve: {chunksDisplayValue}
                                </Typography>
                                <Box
                                    sx={{
                                        px: 1,
                                        color: 'primary.main',
                                        pointerEvents: 'none',
                                        '& .MuiSlider-thumb': {
                                            color: 'primary.main',
                                        },
                                        '& .MuiSlider-track': {
                                            color: 'primary.main',
                                        },
                                        '& .MuiSlider-rail': {
                                            color: 'primary.main',
                                        },
                                    }}
                                >
                                    <Slider
                                        value={chunksDisplayValue}
                                        min={1}
                                        max={20}
                                        step={1}
                                        marks={[
                                            { value: 1, label: '1' },
                                            { value: 5, label: '5' },
                                            { value: 10, label: '10' },
                                            { value: 15, label: '15' },
                                            { value: 20, label: '20' },
                                        ]}
                                        disabled
                                        sx={{
                                            mt: 1,
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 1, fontWeight: 500 }}
                                >
                                    Answer style
                                </Typography>
                                <Chip
                                    label={
                                        answerStyleLabel[
                                            preferences.answer_style
                                        ] ?? preferences.answer_style
                                    }
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        backgroundColor: 'primary.mainLight',
                                    }}
                                />
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 1, fontWeight: 500 }}
                                >
                                    Response mode
                                </Typography>
                                <Chip
                                    label={
                                        responseModeLabel[
                                            preferences.response_mode
                                        ] ?? preferences.response_mode
                                    }
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        backgroundColor: 'primary.light',
                                    }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                {preferences.show_citations ? (
                                    <CheckCircleIcon
                                        color="success"
                                        fontSize="small"
                                    />
                                ) : (
                                    <CancelIcon
                                        color="error"
                                        fontSize="small"
                                    />
                                )}
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                >
                                    Show citations
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
}
